//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import { apis } from 'utils/lib/apis';

//  -- Actions ------------------------
import * as LessonPlanActions from "./actions";
import { logToSentry } from "utils/networkErrorUtils";

//  ----------------------------------------------------------------------------
//  Thunks
//  ----------------------------------------------------------------------------

//  -- Create or Update Lesson Plan ----------------
export const handleCreateOrUpdateLessonPlan = (lessonPlanData) => async (dispatch) => {
  try {
    const { chapter, page_id, created_by, parent_id, content, user_type } = lessonPlanData;
    
    const response = await apis.post("lesson_plan", `/create_or_update`, {
      body: {
        ...lessonPlanData,
        chapter,
        page_id,
        created_by,
        parent_id,
        content,
        user_type
      },
    });

    if (response.success && response.data) {
      const lessonPlan = response.data;
      await dispatch(LessonPlanActions.createOrUpdateLessonPlanSuccess(lessonPlan));
      return lessonPlan;
    } else {
      return { error: response.message || `Unable to create or update lesson plan` };
    }
  } catch (error) {
    if (error && error.message !== "Network Error") {
      logToSentry({
                message: 'LP001 Error creating or updating lesson plan',
                error,
                module: 'LESSON_PLAN',
                operation: 'LIST_EXAM_CLASS_ORGANIZATION_USER_LESSON_PREFERENCE'
            });
    }
    return error;
  }
};



//  -- List Lesson Plans by Book ----------------
export const handleListLessonPlanByBook = (bookId) => async (dispatch) => {
  try {
    if (!bookId) {
      return { error: 'Book ID is required to fetch lesson plans' };
    }

    const response = await apis.get("lesson_plan", `/list_by_book/${bookId}`);

    if (response.success && response.data) {
      const lessonPlans = response.data;
      await dispatch(LessonPlanActions.listLessonPlanByBookSuccess(lessonPlans));
      return lessonPlans;
    } else {
      const errorMessage = response.message || 'Unable to fetch lesson plans. Please try again.';
      return { error: errorMessage };
    }
  } catch (error) {
    let errorMessage = 'Failed to fetch lesson plans. Please check your connection and try again.';
    
    if (error && error.message) {
      if (error.message === "Network Error") {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.response) {
        const status = error.response.status;
        if (status === 404) {
          errorMessage = 'Lesson plans not found for this book.';
        } else if (status === 403) {
          errorMessage = 'You do not have permission to access lesson plans.';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message && error.message !== "Network Error") {
        errorMessage = error.message;
      }
      
      if (error.message !== "Network Error") {
        logToSentry({
          message: 'LP003 Error listing lesson plans by book',
          error,
          module: 'LESSON_PLAN',
          operation: 'LIST_EXAM_LESSON_LESSON_PREFERENCE_BOOK_BOOK'
        });
      }
    }
    
    return { error: errorMessage };
  }
};

//  -- List Lesson Plans by Book and User ----------------
export const handleListLessonPlanByBookAndUser = (bookId, userId) => async (dispatch) => {
  try {
    const response = await apis.get("lesson_plan", `/list_by_book_and_user/${bookId}/${userId}`);

    if (response && response.success && response.data) {
      const lessonPlans = response.data;
      await dispatch(LessonPlanActions.listLessonPlanByBookAndUserSuccess(lessonPlans));
      return lessonPlans;
    } else {
      return { error: response?.message || `Unable to list lesson plans by book and user` };
    }
  } catch (error) {
    if (error && error.message !== "Network Error") {
      logToSentry({
                message: 'LP004 Error listing lesson plans by book and user',
                error,
                module: 'LESSON_PLAN',
                operation: 'LIST_EXAM_LESSON_LESSON_PREFERENCE_BOOK_BOOK_ACTIVITY_USER'
            });
    }
    return error;
  }
};

//  -- Delete Lesson Plans by Book ----------------
export const handleDeleteLessonPlanByBook = (id) => async (dispatch) => {
  try {
    const response = await apis.del("lesson_plan", `/delete_by_book/${id}`);

    if (response.success) {
      // Clear all lesson plans for this book from the store
      await dispatch(LessonPlanActions.deleteLessonPlanSuccess(response.data));
      return response;
    } else {
      return { error: response.message || `Unable to delete lesson plans by book` };
    }
  } catch (error) {
    if (error && error.message !== "Network Error") {
      logToSentry({
                message: 'LP005 Error deleting lesson plans by book',
                error,
                module: 'LESSON_PLAN',
                operation: 'LIST_EXAM_DATA_LESSON_PREFERENCE_BOOK_BOOK'
            });
    }
    return error;
  }
};

//  -- Delete Lesson Plans by Book and User ----------------
export const handleDeleteLessonPlanByBookAndUser = (id) => async (dispatch) => {
  try {
    const response = await apis.del("lesson_plan", `/delete/${id}`);

    if (response.success) {
      // Clear lesson plans for this book and user from the store
      await dispatch(LessonPlanActions.deleteLessonPlanSuccess(response.data));
      return response;
    } else {
      return { error: response.message || `Unable to delete lesson plans by book and user` };
    }
  } catch (error) {
    if (error && error.message !== "Network Error") {
      logToSentry({
                message: 'LP006 Error deleting lesson plans by book and user',
                error,
                module: 'LESSON_PLAN',
                operation: 'LIST_EXAM_DATA_LESSON_PREFERENCE_BOOK_BOOK_ACTIVITY_USER'
            });
    }
    return error;
  }
};

//  -- Generate English Content for Lesson Plan ----------------
export const handleGenerateEnglishLessonPlan = (lessonPlanId, userId, bookId,isAdmin) => async (dispatch) => {
  try {
    
    const response = await apis.post("lesson_plan", `/translate_lesson_plan`, {
      body: {
       lesson_plan_id: lessonPlanId, 
       user_id: userId,
       is_admin: isAdmin
      },
    });

    if (response.success && response.data) {
      // Refresh the lesson plans list
      await dispatch(handleListLessonPlanByBookAndUser(bookId, userId));
      return response.data;
    } else {
      return { error: response.message || `Unable to generate English content` };
    }
  } catch (error) {
    if (error && error.message !== "Network Error") {
      logToSentry({
                message: 'LP007 Error generating English content',
                error,
                module: 'LESSON_PLAN',
                operation: 'LIST_EXAM_GRADE_EXAM_CLASS'
            });
    }
    return error;
  }
};
//  -- Generate English Content for Lesson Plan ----------------
export const handleGenerateEnglishLessonPlanForAdmin = (lessonPlanId, bookId) => async (dispatch) => {
  try {

    const response = await apis.post("lesson_plan", `/translate_lesson_plan_admin`, {
      body: {
        lesson_plan_id: lessonPlanId
      },
    });

    if (response.success && response.data) {
      return response.data;
    } else {
      return { error: response.message || `Unable to generate English content` };
    }
  } catch (error) {
    if (error && error.message !== "Network Error") {
      logToSentry({
        message: 'LP007 Error generating English content',
        error,
        module: 'LESSON_PLAN',
        operation: 'LIST_EXAM_GRADE_EXAM_CLASS'
      });
    }
    return error;
  }
};